/**
 * Access Groups - list grouped by entity (A-Z), names A-Z within each.
 *
 * Lists groups from the backend and routes create/edit actions to the group
 * editor, where mutations and dependency checks are handled.
 *
 * Data type filter lives in the column header (same pattern as Sync History).
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState, renderUnavailableState } from "../components/states";
import { renderRelativeTime } from "../components/dateTime";
import { HELP } from "../components/tooltip";
import { ENTITY_ORDER, entityLabel, sortAccessGroups } from "../utils/format";
import { listGroups } from "../services/admin";
import { ApiError } from "../services/apiClient";
import { restoreWindowScroll, stashGroupsListState, takeGroupsListState } from "../app/viewState";
import type { AuthGroup } from "../types/api";

export class AccessGroupsPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading access groups…")]);
  private entitySelect!: HTMLSelectElement;
  private entity = "";
  private allGroups: AuthGroup[] = [];
  private navigate: (path: string) => void = () => undefined;
  private pendingScrollY: number | null = null;

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.navigate = navigate;

    const restored = takeGroupsListState();
    if (restored) {
      this.entity = restored.entity;
      this.pendingScrollY = restored.scrollY;
    }

    this.entitySelect = headerSelect(
      "Filter by data type",
      [
        el("option", { value: "" }, ["Data type"]),
        ...ENTITY_ORDER.map((e) => el("option", { value: e }, [entityLabel(e)])),
      ],
      (value) => {
        this.entity = value;
        this.renderFiltered();
      }
    );
    this.entitySelect.value = this.entity;
    syncHeaderActive(this.entitySelect, this.entity !== "");

    const clearButton = el(
      "button",
      {
        className: "btn-link",
        type: "button",
        onclick: () => this.clearFilters(),
      },
      ["Clear filters"]
    );

    const toolbar = el("div", { className: "toolbar toolbar--range" }, [
      el("div", {}),
      clearButton,
    ]);

    const createButton = el(
      "button",
      {
        className: "btn btn-primary btn-sm",
        type: "button",
        onclick: () => this.navigate("/groups/new"),
      },
      ["Create access group"]
    );

    const refreshButton = el(
      "button",
      { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.load() },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Access Groups",
          titleHelp: HELP.accessGroup,
          subtitle: "Reusable column-level permissions per entity.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton, createButton],
        }),
        el("div", { className: "panel panel--standard" }, [toolbar, this.body]),
      ])
    );

    void this.load();
  }

  destroy(): void {
    stashGroupsListState({ entity: this.entity, scrollY: window.scrollY });
  }

  private clearFilters(): void {
    this.entity = "";
    this.entitySelect.value = "";
    syncHeaderActive(this.entitySelect, false);
    this.renderFiltered();
  }

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading access groups…"));
    try {
      this.allGroups = await listGroups();
      this.renderFiltered();
      if (this.pendingScrollY !== null) {
        restoreWindowScroll(this.pendingScrollY);
        this.pendingScrollY = null;
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(this.body, renderUnavailableState(err.message));
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private renderFiltered(): void {
    if (!this.allGroups.length) {
      mount(this.body, renderEmptyState("No access groups yet."));
      return;
    }

    const filtered = this.entity
      ? this.allGroups.filter((group) => group.entity === this.entity)
      : this.allGroups;

    if (!filtered.length) {
      mount(
        this.body,
        el("div", {}, [
          this.renderTable([]),
          renderEmptyState("No access groups match this data type."),
        ])
      );
      return;
    }

    mount(
      this.body,
      this.renderTable(sortAccessGroups(filtered))
    );
  }

  private renderTable(groups: AuthGroup[]): HTMLElement {
    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["Name"]),
          filterHeader(this.entitySelect),
          el("th", { scope: "col", className: "data-table__col--numeric" }, ["Columns"]),
          el("th", { scope: "col", className: "data-table__col--relative" }, ["Updated"]),
        ]),
      ]),
      el(
        "tbody",
        {},
        groups.map((group) =>
          el(
            "tr",
            {
              className: "data-table__row--nav",
              tabIndex: 0,
              role: "button",
              "aria-label": `${group.name} - ${entityLabel(group.entity)}, ${group.columns.length} columns`,
              onclick: () => this.navigate(`/groups/${group.id}`),
              onkeydown: (ev: KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  this.navigate(`/groups/${group.id}`);
                }
              },
            },
            [
              el("td", {}, [group.name]),
              el("td", {}, [entityLabel(group.entity)]),
              el("td", { className: "tnum" }, [String(group.columns.length)]),
              el("td", {}, [renderRelativeTime(group.updatedAt)]),
            ]
          )
        )
      ),
    ]);

    return el("div", { className: "data-table-wrap" }, [table]);
  }
}

function headerSelect(
  ariaLabel: string,
  options: HTMLElement[],
  onChange: (value: string) => void
): HTMLSelectElement {
  const select = el(
    "select",
    {
      className: "input input--header",
      "aria-label": ariaLabel,
      onclick: stopRow,
      onchange: (ev: Event) => {
        const value = (ev.target as HTMLSelectElement).value;
        syncHeaderActive(select, value !== "");
        onChange(value);
      },
    },
    options
  ) as HTMLSelectElement;
  return select;
}

function syncHeaderActive(control: HTMLElement, active: boolean): void {
  control.classList.toggle("input--header-active", active);
}

function filterHeader(control: HTMLElement): HTMLElement {
  return el("th", { scope: "col", className: "data-table__th-filter data-table__col--entity" }, [control]);
}

function stopRow(ev: Event): void {
  ev.stopPropagation();
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
